import { useEffect, useState } from "react";
import { apiGet, apiPut } from "../api/client";
interface CredentialStatus {
    present?: boolean;
    has_cookie?: boolean;
    has_access_token?: boolean;
    user_agent?: string | null;
    updated_at?: string | null;
    source?: string;
    error?: string;
}
/**
 * Control Tower credential rotation for SaveTicker (advisor policy §2).
 *
 * Values are submitted once and stored in Redis; they are never returned by
 * the API or rendered back into the form. Cookie issuance must be done from
 * the Beelink VPN exit IP with the browser UA that will be sent by the
 * collector (see the deployment runbook §8.2).
 */
export default function SaveTickerCredentialCard() {
    const [status, setStatus] = useState<CredentialStatus | null>(null);
    const [cookie, setCookie] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [userAgent, setUserAgent] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const load = async () => {
        try {
            const data = await apiGet<CredentialStatus>("/saveticker/credentials");
            setStatus(data);
            setError(data.error ? data.error : null);
        }
        catch (err) {
            setError(String(err));
        }
    };
    useEffect(() => {
        load();
    }, []);
    const submit = async () => {
        setSaving(true);
        setMessage(null);
        setError(null);
        try {
            const data = await apiPut<CredentialStatus>("/saveticker/credentials", {
                cookie: cookie || null,
                access_token: accessToken || null,
                user_agent: userAgent || null,
            });
            setStatus(data);
            setCookie("");
            setAccessToken("");
            setMessage("자격이 Redis에 등록되었습니다. 값은 다시 표시되지 않습니다.");
        }
        catch (err) {
            setError(String(err));
        }
        finally {
            setSaving(false);
        }
    };
    const hasInput = cookie.length > 0 || accessToken.length > 0;
    return (<div className="card credential-card">
      <div className="card-title-row">
        <h3 className="card-title">SaveTicker 자격 증명 관리</h3>
        <span className="card-title-meta">
          {status?.present
            ? `등록됨 · ${status.updated_at || "시각 미상"} · ${status.source || "control_tower"}`
            : status ? "화면 등록 없음 · 환경 설정 확인" : "등록 상태 확인 필요"}
        </span>
      </div>
      <div className="muted-note">
        Beelink VPN(exit node) IP로 발급한 세션 쿠키와, 쿠키를 발급한 브라우저와 동일한
        User-Agent를 입력해야 WAF 지문이 일치합니다. 입력값은 저장 후 화면·로그·API
        응답에 다시 나타나지 않습니다.
      </div>
      <div className="grid grid-2" style={{ marginTop: "var(--space-3)" }}>
        <label>
          쿠키 (cookie)
          <input type="password" autoComplete="off" value={cookie} onChange={(event) => setCookie(event.target.value)} placeholder="name=value; name2=value2"/>
        </label>
        <label>
          access token (선택)
          <input type="password" autoComplete="off" value={accessToken} onChange={(event) => setAccessToken(event.target.value)} placeholder="JWT"/>
        </label>
      </div>
      <label style={{ display: "block", marginTop: "var(--space-3)" }}>
        User-Agent (쿠키 발급 브라우저와 동일하게)
        <input type="text" value={userAgent} onChange={(event) => setUserAgent(event.target.value)} placeholder={status?.user_agent || "Mozilla/5.0 ..."}/>
      </label>
      <div className="btn-row" style={{ marginTop: "var(--space-3)" }}>
        <button className="btn-primary" onClick={submit} disabled={saving || !hasInput}>
          {saving ? "등록 중..." : "자격 등록"}
        </button>
        <button className="btn-refresh" onClick={load} disabled={saving}>
          상태 새로고침
        </button>
      </div>
      {message && <div className="muted-note" style={{ color: "var(--green)" }}>{message}</div>}
      {error && <div className="muted-note" style={{ color: "var(--red)" }}>{error}</div>}
    </div>);
}
